import React, {useEffect} from "react";
import {useTheme} from "@mui/material/styles";
import Box from "@mui/material/Box";
import {Link} from "react-router-dom";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import TimelineIcon from "@mui/icons-material/Timeline";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import {useTranslation} from "react-i18next";
import {UserContext} from "../../App";
import {fetchData} from "../../utils/api";

export default function ExperimentTypeSelector() {
    const theme = useTheme();
    const {t} = useTranslation();
    const {
        datasets,
        currentDatasetIdx
    } = React.useContext(UserContext);

    const [trainedModelsExist, setTrainedModelsExist] = React.useState(false);


    // Card data for easy extension
    const cards = [
        {
            id: "classification",
            title: t("experimentTypeSelector.ClassificationTitle"),
            description: t("experimentTypeSelector.ClassificationDescription"),
            icon: <LocalFloristIcon/>,
            link: "/experiment-configuration?type=Classification",
            iconBg: theme.palette.secondary.light,
            iconColor: theme.palette.secondary.main,
        },
        {
            id: "regression",
            title: t("experimentTypeSelector.RegressionTitle"),
            description: t("experimentTypeSelector.RegressionDescription"),
            icon: <ScatterPlotIcon/>,
            link: "/experiment-configuration?type=Regression",
            iconBg: theme.palette.secondary.light,
            iconColor: theme.palette.secondary.main,
        },
        {
            id: "forecast",
            title: t("experimentTypeSelector.ForecastTitle"),
            description: t("experimentTypeSelector.ForecastDescription"),
            icon: <TimelineIcon/>,
            link: "/experiment-configuration?type=Forecast",
            iconBg: theme.palette.secondary.light,
            iconColor: theme.palette.secondary.main,
        },
        {
            id: "error",
            title: t("experimentTypeSelector.ErrorModelTitle"),
            description: t("experimentTypeSelector.ErrorModelDescription"),
            icon: <QueryStatsIcon/>,
            link: "/experiment-configuration?type=Error%20Model",
            iconBg: theme.palette.secondary.light,
            iconColor: theme.palette.secondary.main,
        },
    ];

    const fetchExperiments = (datasetName) => {
        // fetch Experiments to check if trained models exist
        fetchData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiments`
        ).then((dataset) => {
            if (dataset.experiments.length !== 0) {
                dataset.experiments.forEach((experiment) => {
                    const numTrainedModels = experiment.experimentProgress.trained
                    // if at least one trained model exists, show Error Model card
                    if (numTrainedModels > 0) {setTrainedModelsExist(true);}
                });

            } else {
                setTrainedModelsExist(false);
            }
        });
    };

    useEffect(() => {
        if (datasets.length >= 1) {
            fetchExperiments(datasets[currentDatasetIdx].datasetName);
        }
    }, [datasets]);


    return (
        <Box
            id="experiment-creator-container"
            sx={{
                padding: {xs: theme.spacing(3, 1), md: theme.spacing(8, 2)},
                background: theme.palette.background.default,
                minHeight: "100vh",
            }}
        >
            <Typography
                variant="h3"
                sx={{
                    fontWeight: "bold",
                    textAlign: "center",
                    color: theme.palette.primary.main,
                    mb: 2,

                    display: "inline-block",
                    paddingBottom: "0.3rem",
                }}
            >
                {t("experimentTypeSelector.PageTitle")}
            </Typography>
            <Typography variant="h4" gutterBottom>
                {t("experimentTypeSelector.PageInstructions")}
            </Typography>
            <Grid
                container
                spacing={3}
                justifyContent="center"
                alignItems="stretch"
                sx={{mt: 2}}
            >
                {cards.map((card) => (
                     (card.id!=="forecast" ?
                            <Grid item key={card.id} lg={4} xs={12} sm={6}>
                                <Link
                                    className="experiment-type-selector"
                                    to={card.link}
                                    style={{textDecoration: "none",
                                        pointerEvents: card.id === "error" && !trainedModelsExist ? "none" : "auto"}}
                                >
                                    <Card
                                        sx={{
                                            maxWidth: 800,
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            borderRadius: 5,
                                            boxShadow: 3,
                                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                            "&:hover": {
                                                transform: "translateY(-5px)",
                                                boxShadow: 6,
                                            },
                                            opacity: card.id === "error" && !trainedModelsExist ? 0.4 : 1,
                                        }}
                                        elevation={4}
                                    >
                                        <CardContent sx={{flexGrow: 1,}}>
                                            <Grid
                                                container
                                                spacing={1}
                                                alignItems="center"
                                                sx={{mb: theme.spacing(2)}}
                                            >
                                                <Grid item>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: card.iconBg,
                                                            color: card.iconColor,
                                                            width: 56,
                                                            height: 56,
                                                            boxShadow: 2,
                                                        }}
                                                    >
                                                        {card.icon}
                                                    </Avatar>
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="h6" gutterBottom>
                                                        {card.title}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Typography variant="body2" color="text.secondary">
                                                {card.description}
                                            </Typography>
                                        </CardContent>
                                        <CardActions>
                                            <Button size="small">
                                                {t("experimentTypeSelector.CreateExperimentButton")}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Link>
                            </Grid>

           : "")
                ))}
            </Grid>
        </Box>
    );
}
